<template>
    <article id="page-manual">
        <section class="categories">
            <ul>
                <li v-for="(category, key) in categories"
                    :class="{'category-label': true, 'selected': key === selectedCategory}"
                    @click="setCategory(key)"
                    :key="key"
                >
                    {{key}}
                </li>
            </ul>
        </section>

        <section class="manual-content" v-if="currentCategory">
            <div class="manual-text" v-if="currentCategory">
                <div class="manual-section">
                    <markdown-content :content="currentCategory"></markdown-content>
                </div>
            </div>
        </section>
    </article>
</template>

<script>
    export default {
        name: "manual",
        computed: {
            currentCategory() {
                return this.categories[this.selectedCategory];
            }
        },

        mounted() {
            let urlCategory = this.$route.params.section;

            if (urlCategory) {
                urlCategory = urlCategory.replace(/-/ig, ' ');
                this.selectedCategory = urlCategory;
            }
        },

        methods: {
            setCategory(category) {
                this.selectedCategory = category;
                category = category.replace(/ /ig, '-');
                this.$router.push('/Manual/' + category);
            }
        },

        data() {
            return {
                selectedCategory: 'Getting Started',

                categories: {
                    'Getting Started': `# Getting Started

This will cover the Steps found under *Tools -> Boomerang2D -> Getting Started*

Do the following steps in order:

- Set up the required Layers
- Edit GameProperties.cs to set your Pixels Per Unit, Aspect Ratio, and Scale
- Add a GameObject to the Scene named 'Boomerang2D' and attach the Boomerang Reference Database script
- Go to Tools *-> Boomerang2D -> Build Reference Database*
- Add a Start Game script to the Scene with a call to Boomerang2D.Initialize() and load a map

And that's everything you need to do to set up your project!

After that, explore the various editor windows and start generating your content.
`,


                    'Actors': `# Actors

# Overview

Actors are most interactive objects in your Game. This includes Player Characters, NPCs, Enemies, Moving platforms, Pick-up Items, and so on.

Actors are built in the *Tools -> Boomerang2D -> Actor Studio.*

They are made up of the following elements:

- A sprite sheet
- Bounding Boxes and Raycast Properties
- Stats
- The State Machine
- Particle Effects
- Weapons
- Interaction Events
- Children Actors

There are also some general concepts that are used throughout the Actor creation process:

- Triggers
- Events
- Calculated Values

# General Concepts

## Triggers

Triggers are classes that compute conditional statements, and return a true or false value. Triggers are used throughout the Actor Studio.

Boomerang2D contains a number of pre-written Triggers, and custom Triggers can be easily written and integrated into the Actor Studio.

Places where Triggers are used:

- State Transitions
- State Animations
- State Modifications
- State Weapons
- State Entry Events
- Interaction Events

## Events

Events are scripts that can be executed given conditions.

Boomerang2D contains a number of pre-written Events, and custom Events can be easily written and integrated into the Actor Studio.

Places where Events are used:

- State Entry Events
- Interaction Events

## Calculated Values

Some properties of State Behaviors, Triggers, and Events are Calculated Values. These properties have a [icon:plus] icon after the initial value field. Clicking this icon will add another mathematical row to the value.

Each row is calculated sequentially in order, and can contain the Actor's Stats and Gameflags. Additionally, the final calculated value can be clamps to a minimum or a maximum value.

The total value of the property is re-calculated every time that value is accessed, allowing real-time modification of these properties.


# Sprite Sheet

Sprite sheets are a single image containing all of the frames of animation for your Actor. They should be laid out in a grid with each cell containing an entire frame.

Define the Width and the Height of each cell in the Actor Properties tab, and then import the Sprite Sheet. Boomerang 2D will automatically cut your image into equally sized cells.

# Bounding Boxes and Raycast Properties

## Bounding Boxes

Bounding Boxes are rectangular regions that allow the player to detect and interact with level geometry, other actors, weapons, and so on.

You can also have multiple Bounding Boxes.

Values set in the Bounding Boxes tab are only the default values. Each of these values can be overridden on each State Animation Frame. When adding a new Animation Frame, these default values will automatically populate for that frame.

Properties:

- Enabled - Enabled and disabled the Bounding Box
- Size - Width and Height in pixels
- Offset - an Offset of (0,0) will center the Bounding Box on the actor both horizontally and vertically. This Offset is measured in Pixels.
- Flags - The list of Flags is read in various Triggers and Events and is used to control behavior. For example, you don't want your player taking damage from all other bounding boxes it overlaps with, only ones with a specific flag, such as "enemy" or "spike".

## Raycasting

Each Bounding Box can cast rays in one or more of the four orthographic directions (Up, Right, Down, Left). Rays are used to collect information about the level geometry and other actors which do not overlap your Actor.

The most common use of Raycasting is to determine the distance between the Actor and a Solid tile so that the engine can control that type of collision.

Rays are somewhat expensive, so disable them if you don't need them for your actor.

The properties for each ray control the start position, length, and quantity of the rays.

# Stats

Stats are basically variables attached to your Actor. These values can be read and written to through various Triggers and Events, as well as Calculated Values.

# Particle Effects

Created Particle Effect objects should be placed in the Boomerang2D/Content/ParticleEffects folder.

The Particle Effects tab will show a drop-down list of all Particle Effects found in the correct folder. If this doesn't show correctly, rebuild the Reference Database and refresh the Actor Studio.

For each Particle Effect you want to bind to the actor, click the [icon:plus] icon next to the drop down.

Particle Effects are disabled by default, and are enabled per each animation frame. View the State Animations section for more information.

# Weapons

The Attached Weapons tab will show a drop-down list of all created Weapons. If this doesn't show correctly, rebuild the Reference Database and refresh the Actor Studio.

For each Weapon you want to bind to the actor, click the [icon:plus] icon next to the drop down.

Weapons are disabled by default, and are enabled through Weapon Triggers found on specific States. View more information about this in the Weapon Triggers section below.

# Children Actors

You can attach other Actors as children. Children Actors will update their reference point to the position of the parent actor always.

Do no make two Actors children of each other, or you will end up with an infinite loop where each actor adds the other indefinitely.

# States

The State Machine controls the core behavior of your Actor. It is made up of one ore more States, each with their own properties.

## State Properties

### Behavior Classes

Each State requires a Behavior Class. This is the script that informs the Actor how to behave while in this State. Each Behavior Class has a Properties Class associated with it.

Boomerang2D contains several basic Behavior Classes, and new Classes can be written and integrated into the Actor Studio.

#### Other State Properties

Aside from the properties associated with the specific State Behavior, there are several properties available for all States.

- Can Be Grounded - Used in side-view games, this allows the Actor to be Grounded, meaning it is standing on the Ground. The IsGrounded property can be read from various Triggers and State Behaviors.
- Collides with Geometry - The Actor will collect information from all tiles that are hit by its Rays. This is required if you want your Actor to collide with Solid tiles.
- Collides with Actors - The Actor will collect information from other actors that are hit by its Rays.
- Overlaps Geometry - The Actor will collect all tiles that overlap it's Bounding Boxes and store the tiles' information.
- Overlaps Other Actors - The Actor will collect all other Bounding Boxes that overlap it's own Bounding Boxes and store the other Bounding Boxes' information.
- Overlaps Weapons - The Actor will collect information from any weapons that overlap it's Bounding Boxes.

These can be expensive operations and it's recommended that they're turned off if they aren't needed.

## Transition Triggers

Transition Triggers are the collection of conditionals that determine when the Actor should transition from this State into another State. For example, you would want to transition from an Idle state to a Walking state if the player holds an input.

To create a Transition, select the State you want to transition to and click the [icon:plus] icon.

For your transition, click the "Add Condition" button. You can have as many conditions as you want. When **all** of the conditions are met, the State Machine will move the Actor into the new State. In this way, transitions work as AND conditions. To have an OR condition, simply add a new transition to the same state.

## Animations

Each State can contain multiple animations. The dropdown and [icon:plus] icon at the top allows you to view and add them.

### Animation Properties:

- Bound to Global Time -  When disabled, animations will start at the first frame when the State begins. When enabled, the animation will start on a frame calculated by the time elapsed since the game started. This allows all animated tiles and actors to be in sync, regardless of when they started their individual lifespans.
- Bound to Weapons - When set, the animation's start point and duration is re-scaled to match the current playing point and duration of the specified weapon
- Loops Indefinitely - If enabled, the animation will loop forever. If disabled, the animation will defer to the FixedLoopCount property.
- Final Frame Id - When the animation finishes its final loop, this is the frame the animation display.
- Start on Existing Sprite -  When enabled, the animation will start on the same frame number that the previous animation left off on, even if the previous animation was in another State.

### Frames

Each frame has control over its own properties, Bounding Boxes, Raycasting, Particle Effects, and Play Conditions.

#### Frame Properties

- Duration - The duration in seconds that this frame will play.
- Sprite Frame - This is the index of the frame cell from the Sprite Sheet imported in the Actor Properties tab.
- Flip Horizontal/Vertical - Flips the sprite in the given axis.

#### Bounding Boxes and Raycasting

When a new frame is added, the properties of the Bounding Boxes is populated with the default values from the Bounding Boxes tab, but each value can be controlled independently per-frame.

#### Particle Effects

If a Particle Effect has been added to the Actor in the Particle Effects tab, controls for it will be displayed. You can toggle whether it's enabled and it's offset for that frame.

#### Play Conditions

Since a State can have multiple animations, you will need to control which Animation plays. Play Conditions are made with Triggers.

The animation that will end up playing is the one furthest down in the list with all of its Play Conditions met.

## Sound Effects

Each State can have its own Sound Effects. Sound Effects, like Music, should be placed in the *Boomerang2DFramework/Content/Audio folder*. Sound Effects should also be in the .wav format. Other formats may have a delay before playing.

- SFX Pool - A list of all Sound Effects that can be played in this State, labeled by their Name
- Volume - How loud the Sound Effect will play
- Start Time - The delay between when the State is entered and the Sound Effect will play.
- Loops - If set, the Sound Effect will play indefinitely as long as the Actor is in this state.
- Play Count - If Loops is disabled, this controls how many times the Sound Effect will play.
- Kill on State Exit - If Loops is enabled, this will cut a sound effect off if it's playing while the State is exited.
- Play Random - If there are more than one Sound Effects in the SFX Pool, this will randomly select items from the pool. If disabled, the Sound Effects in the SFX Pool will play sequentially.

## Modification Triggers

Some State Behaviors have additional methods that can be accessed through Triggers. These methods have the header [CanBeCalledByModificationTrigger].

## Weapon Triggers

If a Weapon is attached to the Actor through the Attached Weapons tab, you can control when that Weapon is fired through the Weapon Triggers.

The various properties can modify how the Weapon performs. Additionally, the Triggers While Active property will allow the weapon to be fired if it's already firing if enabled. Disable this property to prevent this behavior.

## Entry Events

You can add Events with Triggers that will be checked and executed once, when the State is transitioned into.

# Interaction Events

Interaction Events are Events that are executed given the results of Actor Filter Conditions and Triggers.

When the Match Condition of the Actor Filter is true, and all Trigger condtions are true, then all Events will execute.

## Actor Filter Conditions

This column will allow you to set up filter conditions that search the map for other Actors that match these conditions. At the bottom of this column, you can set how many results you expect to find.

## Triggers

This column is a list of Triggers that must be true to execute the events. These Triggers work the same as they do in the other modules that use them.

## Events

This is a list of Events that are executed when all the conditions are true.

The 'Affects' property determines if the Event should affect the current Actor, or all of the Actors found in the Actor Filter.
`,



                    'Weapons': `# Weapons

# Overview

The Weapon Editor defines Weapons that can be attached to Actors. They are a series of collider animations that play over the top of your Actor and can be triggered through conditions. See the relevant topics in the Actor section of this manual for more information on how this is done.

## Melee Strikes

Weapons are made up of one or more Melee Strikes. Each strike is made up of a collider with Actions that control the properties of the collider over time.

### Properties

- Base Class - This is the collider class. There are currently two collider shapes: Wedge and Box. Circles can be made with the Wedge shape and a Width of 360.
- Start Tile - This is the delay between the weapon being triggered and the weapon starting its animation.

### Actions

Each added Action will play sequentially, and has a Duration, an Ease Mode, and a set of Before-and-After values.

These Actions will define the shape and animation of your Weapon's collider.

                    `,


                    'Tilesets': `# Tilesets

# Overview

Tilesets are used when building Maps. Each Map Layer has its own Tileset, and the Tileset Editor is where you define the properties for that Tileset. Tilesets are made up of an image that's sliced on import, and a series of Tiles with their own properties.

## Tileset Properties

The first thing we do when setting up our Tileset is define Tile Size. Normally you want this to be the same as your Pixels Per Unit, but they can be as small or as large as you want. Afterwards, click the Import Tilesheet Image button and select your image.

Tilesheet Images follow the same rules as Actor Sprite Sheets, in that they should be laid out in a grid with each cell containing an entire tile and with no gaps between them. Importing the Tilesheet Image will automatically slice the image into individual tiles.

The Auto-Generate Tiles option will populate the Tileset with Tiles, where each Tile index is mapped to the the Frame Index from the Tilesheet Image.

The Display Width property will define how many tiles wide your Tileset will appear in both the Tileset Editor and the Map Editor.

## Tiles

To add a new Tile, click the Tiles button and then click "Add a Tile". This will add a Tile to the middle display field. Click the new Tile to view its properties.

### Tile Properties

#### Appearance

Each tile has Frames. The Frame Id is the Index of the Sliced Tilesheet. The "O" button on the left will open the image and allow you to choose a Frame Id visually.

When there are multiple Frames, you can also set the duration of the Frame.

Tiles with multiple frames will animate both in the editor and in the game.

#### Flags

Like Actor Bounding Boxes, Tiles have Flags that can be read from various Triggers and Events. For example, you can set a tile to have a flag named "Ladder" and allow the Actor to enter a "ClimbLadder" state when overlapping this Tile.

Flags will not work without a Collider attached.

#### Collider

Enabled Colliders will ask for a Shape and various orientation options. You can also set Colliders to be Solid-on-Top, on Bottom, on Left, and on Right.

## Stamps

Stamps are collections of Tiles that can be placed and scaled in the Map Editor. They have support for Repeating Rows/Columns so as the Stamp is scaled, the correct tiles will repeat.

To add a new Stamp, click the Stamps button and then click "Add a Stamp"

### Stamp Properties

At the top right corner, you can Name the stamp and define its Width and Height. Under that is the list of created Stamps; Click one to edit it. Finally, under that is a list of your Tiles.

Fill the Stamp by clicking a Tile and then clicking the destination cell in the grid.

You can make  a column or row repeatable by clicking circle arrow icon for that column or row.

## Brushes

Brushes allow you to paint tiles and have them auto-update based on the shape of the paint in the Map Editor.

To add a new Brush, click the Brushes button and then click "Add a Brush"

This works a lot like the Stamp Editor, allowing you to name your brush and fill the spots.

Each spot is a different set of rules for what tile will be displayed based on the shape of the paint.

The icons show which parts of the tile have borders.

                    `,
                    'Maps': `# Maps

# Overview

Maps contain Layers, Tiles, Actors, Views, and Regions. Building a map will load in all of the content in that map and in most cases is all that needs to be done to start the game.



# Map Properties

- Dimensions - The width and height, in units, of the Map.
- Chunk Size - Maps Layers are grouped into chunks. Chunks that are off-screen are disabled during game play for performance. Chunks that are too large or too small will have a performance hit. A good rule of thumb is to make the Chunk Size half of the GameProperties.UnitsWide.
- On-Load Events - a series of Game Events that will execute when the map is first finished loading.

# Tile Editing

To add Tiles to the map, first create a Map Layer.

## Map Layer Properties

- Use Tile Colliders - When disabled, Tiles placed on this layer will be built without colliders.
- Layer Shader - A dropdown of all available Shaders (placed in *Boomerang2DFramework/Content/SpriteShaders* folder). This entire Map Layer will use this Shader.
- Tileset - The Layer's Tileset
- Parallax - The Parallax scrolling value of this Layer. a value of (1,1) will not parallax

## Tiles

Click a Tile from the Tile panel and click the Stage to place it.

Tile Controls:

- Left Click - Selects the Tile underneath the cursor
- Right Click - Deletes the Tile underneath the cursor
- Click and Drag - Moves the Tile
- Shift + Click and Drag - Scales the selected Tile, repeating it.
- Control + Click and Drag - Clone and place the selected Tile.

## Stamps

Click a Stamp from the Stamp panel and click the Stage to place it. Stamps are defined in the Tileset Editor

Tile Controls:

- Left Click - Selects the Stamp underneath the cursor
- Right Click - Deletes the Stamp underneath the cursor
- Click and Drag - Moves the Stamp
- Shift + Click and Drag - Scales the selected Stamp, repeating it according to the Stamp Rules.
- Control + Click and Drag - Clone and place the selected Stamp.

## Brushes

Brushes allow you to paint tiles that auto-update based on the tiles painted around it.

Click a Brush name button to start painting it.

The Brush button will allow clicking and holding to paint individual tiles. The Area button will allow dragging a rectangular area to fill in the whole space.

"Treat Edges as Solid" will treat the map boundary as if its painted in already.

When finished painting, click Apply Brush. Now you can select the entire painted region as a single object.

# Actors

Actors are placed on the same Map Layers that Tiles are placed on.

Select an Actor from the list and click the Stage to place it.

Click on an Actor on the Stage to select it and view its properties.

## Map Placement Properties:

- X, Y - The position of the placed Actor.
- MapId - A unique ID you can use to reference it easily in the Actor Filters. This is also used in the Linked Actor field by other Actors.

## Instance Properties

Instance Properties are properties set to this specific Instance of the Actor and will override any default values.

- Facing Direction - The initial that the Actor will face when first loaded into the map.
- Linked Actors - A list of MapIds of other placed Actors. This list can be accessed through various triggers. This allows you to have one Actor be a Button and another Actor be a Door that slides up when the player steps on the button.

## Default Stats

Each stat on the Actor can be overridden on initialization.
                    `,

                    'Hud Objects': `# Hud Objects

# Overview

Hud Objects are layers that sit on top of the Map Layers and stays in place as the player moves around the Map. They are made up of Ui Elements and displayed Using a Game Event.

# Bitmap Fonts

The Ui Elements that ship with Boomerang 2D rely on Bitmap Fonts. Bitmap Fonts are created in the Bitmap Font Editor found at *Tools -> Boomerang2D -> Bitmap Font Editor*.

The general use of the Bitmap Font Editor is to import an image and manually slice it into regions for each character (Glyph) you plan to use. Each Glyph has the same height but can have variable widths.

After importing and image, click the Add Glyph button to add a new region. The properties of this new region are defined by the settings in the Add Glyph box.

Select an added Glyph to modify its properties.

# Building Hud Objects

The Hud Object Editor is accessed through *Tools -> Boomerang2D -> HUD Object Editor*

## Properties

- Shader - Sets the sprite shader that all Elements in this Hud Object will render with.

## Elements

Ui Elements are single elements that can be positioned on a Hud Object. Boomerang 2D ships with several Hud Elements but new ones can be built and integrated into the Editors when placed in the *Boomerang2DFramework/CustomScripts/UiElements* folder.

Each Element has its own Properties.

## Interaction Events

Interaction Events on Hud Objects are similar to the ones on Actors.

### Triggers and Events

UiTriggers and GameEvents are used for Hud Objects but they function identically to Actor Triggers and Events. Because Triggers/Events read properties directly from the Hud Object, Actor Triggers/Events cannot be used here.

# Displaying Hud Objects

The quickest and easiest way to display a Hud Object is through the On-Load events of a Map, using the Display Hud Object event. Keep in mind that Hud Objects remain even when new maps are loaded. The same Hud Object will not be rendered twice if opened while already open.
                    `,
                    'Custom Scripts': `# Custom Scripts

# Overview

There are a number of systems within the framework that can be expanded on through Custom Scripts. Custom Scripts will be automatically integrated into the various editors.

All custom scripts belong in the appropriate directory within the *Boomerang2dFramework/CustomScripts/...* folder. Its best to look at the included script examples to get a good idea.

# Triggers

Examples:

There are two types of Triggers: ActorTriggers and HudObjectTriggers. Both are built largely the same way.

- Actor Triggers: Boomerang2DFramework\\Framework\\Actors\\TriggerSystem\\Triggers\\CurrentState.cs
- Hud Object Triggers: Boomerang2DFramework\\Framework\\UiManagement\\TriggerSystem\\Triggers\\TimeDisplayed.cs

## Properties Class

These are the properties that your trigger will reference and display in the Editor. The name of the Properties class should be identical to the name of the behavior class with the word "Properties" appended to the end.

For example:
- MyCustomTrigger.cs
- MyCustomTriggerProperties.cs

They should extend off ActorTriggerProperties or HudObjectTriggerProperties appropriately.

Serialize the Class.

List the properties like any other class.

## Behavior Class

Your Trigger class should extend off ActorTrigger or HudObjectTrigger.

### Properties and Constructor

Add a serialized Private property class type-casted to the Properties class defined in the previous step.

Add a Public Constructor with an argument for ActorTriggerProperties or HudObjectTriggerProperties, and set the value of the argument to the previously mentioned Properties class.

### IsTriggered

The IsTriggered method is what is called to validate the condition. The Trigger returns a True or False value depending on the outcome of this method.

Add a Bool method called IsTriggered with the following Arguments:

- Actors:  Actor actor, State state
- Hud Objects:  HudObjectBehavior hudObjectBehavior

# Events

There are two types of Triggers: ActorEvents and GameEvents. Both are built largely the same way.

Examples:

- Actor Event: Boomerang2DFramework\\Framework\\Actors\\ActorEvents\\Events\\ChangeState.cs
- Game Event: Boomerang2DFramework\\Framework\\GameEvents\\Events\\BackgroundMusicPlay.cs

## Properties Class

These are the properties that your Event will reference and display in the Editor. The name of the Properties class should be identical to the name of the behavior class with the word "Properties" appended to the end.

For example:
- MyCustomEvent.cs
- MyCustomEventProperties.cs

They should extend off ActorEventProperties or GameEventProperties appropriately.

Serialize the Class.

List the properties like any other class.

## Behavior Class

Your Event class should extend off ActorEvent or GameEvent.

### Properties and Constructor

Add a serialized Private property class type-casted to the Properties class defined in the previous step.

Add a Public Constructor with an argument for ActorEventProperties or GameEventProperties, and set the value of the argument to the previously mentioned Properties class.

### ApplyOutcome

This method is executed when the Event is meant to be executed. This contains the main logic of the Event.

GameEvents take no arguments.

Actor Events take the following Arguments: Actor targetActor, Actor sourceActor

The targetActor is the Actor calling the Event, and the sourceActor is the Actor that the event affects. In many cases, these are the same Actor.




# Actor Finder Filters

Example:

- Actor Event: Boomerang2DFramework\\Framework\\Actors\\ActorFinderFilters\\Filters\\CurrentState.cs

## Properties Class

These are the properties that your Filter will reference and display in the Editor. The name of the Properties class should be identical to the name of the behavior class with the word "Properties" appended to the end.

For example:
- MyCustomActorFilter.cs
- MyCustomActorFilterProperties.cs

They should extend off ActorFinderFilterProperties.

Serialize the Class.

List the properties like any other class.

## Behavior Class

Your Filter class should extend off ActorFinderFilter.

### Properties and Constructor

Add a serialized Private property class type-casted to the Properties class defined in the previous step.

Add a Public Constructor with an argument for ActorFinderFilterProperties, and set the value of the argument to the previously mentioned Properties class.

### FetchMatchingActors

This method is the logic of the Filter and takes two arguments: Actor originActor, List[Actor] actorCatalog

It returns a list of the matching Actors from the Actor Catalog




# Actor States

Actor States are behaviors.

Example:
- Boomerang2DFramework\\Framework\\Actors\\States\\MoveOrtho.cs

## Properties Class

These are the properties that your State will reference and display in the Editor. The name of the Properties class should be identical to the name of the behavior class with the word "Properties" appended to the end.

For example:
- MyCustomState.cs
- MyCustomStateProperties.cs

They should extend off ActorStateProperties.

## Behavior Class

Your behavior class should extend off ActorState.

### OnEnterState

This method is called once when the StateManager switches the Actor to this State

### OnExitState

This method is called once when the StateManager switches to another State from this State

### ProcessSetUpFrameState

This method is called at the start of every frame while the Actor is in this State

### ProcessState

This method is called once every frame and is where the meat of the Behavior lives

### ProcessPostFrameState

This method is called at the end of the frame while the Actor is in this state



# Ui Elements

Example:

Boomerang2DFramework\\Framework\\UiManagement\\UiElements\\Behaviors\\ChoiceBoxBehavior.cs
Boomerang2DFramework\\Framework\\UiManagement\\UiElements\\Editor\\ChoiceBoxEditor.cs
Boomerang2DFramework\\Framework\\UiManagement\\UiElements\\Properties\\ChoiceBox.cs

Ui Elements are made up of three separate classes. The Behavior Class, the Properties Class, and the Editor Class

They are placed in individual folders and named accordingly:

- MyCustomElement.cs (Behavior)
- MyCustomElementProperties.cs (Properties)
- MyCustomElementEditor.cs (Editor)

The Behavior class is the functionality of the Element in-game.

The Properties Class contains all the Properties for the class.

The Editor Class contains information about rendering the element in the Hud Object Editor.

                    `,
                }
            }
        }
    }
</script>